// Layer 2 (Engine orchestration): the Advisory route's flow read for every
// surface that paints a figure from it — the Advisory strip itself, the project
// home's Advisory index row (polish.P23 #710) and the rail badge (#732).
//
// It gathers exactly the inputs `flowLandingRead` needs — the session diagnosis,
// the session cases, actions and FMEA reviews, the replay frames, the case stage
// timelines and the previous shift's excursion count — so every reference and
// the Advisory strip read one function over one basis. The Advisory route adds
// the three inputs only its own stages render (the Diagnosis stage's
// investigation-run rows, the Value stage's outcome filter and the floor seats'
// money guard); the reference surfaces leave them at their defaults.
//
// Returns `null` until every case fixture's frames are in hand, the same gate
// the Advisory strip uses before it paints a figure. `scripts/ui-probes/
// project-home-ui.mjs` asserts the home row equals the Advisory route's Action
// cell at the initial state and after an in-session approval.

import { useMemo } from 'react';
import { CASES, FAILURE_MODES } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { DRIVER_TREE_SEED } from '@/versions/v1/shared/bunker-hill-driver-tree-seed';
import { TAG_MAPPING } from '@/versions/v1/shared/bunker-hill-spc';
import { addMinutes, shiftOf } from '@/versions/v1/shared/clock.derive';
import { diagnose, type Diagnosis } from '@/versions/v1/shared/DiagnosisEngine';
import { excursions } from '@/versions/v1/shared/SpcEngine';
import { mergeCasesForThreading } from '@/versions/v1/shared/threads';
import { useAdvisory } from '@/versions/v1/shared/AdvisoryProvider';
import { useScenario } from '@/versions/v1/shared/ScenarioProvider';
import { useAnalysisReplays } from '@/versions/v1/shared/useAnalysisReplay';
import { useAgentTrace } from '@/versions/v1/shared/useAgentTrace';
import { fmeaActionApproval } from '@/versions/v1/shared/FmeaLearningEngine';
import type { Case, FMEAUpdateReview, RecommendedAction } from '@/versions/v1/schema/case';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import type { CaseStageTimeline } from '@/versions/v1/shared/caseStage';
import {
  caseReplayFixtures,
  casesWithSession,
  fmeaReviewsWithSession,
  recommendedActionsWithSession,
  recordedInvestigationCase,
} from '@/versions/v1/modules/advisory/Scene.transform';
import {
  analysisFramesForPipeline,
  buildCaseStageTimeline,
  flowLandingRead,
  type FlowLandingRead,
  type FlowLandingReadInput,
} from '@/versions/v1/modules/advisory/FlowLanding.transform';
import {
  investigationRunRows,
  recordedRunForTrace,
} from '@/versions/v1/modules/advisory/InvestigationRun.transform';

/** The inputs only the Advisory route's own stages render; the reference
 *  surfaces (project home, rail) leave every one at its default. */
export type AdvisoryFlowReadOptions = {
  /**
   * K15 / D24: the Diagnosis stage's investigation runs, derived from the
   * recorded trace. Only the Advisory route renders them, so the reference
   * surfaces ask for no trace and make no request for one.
   */
  withInvestigationRuns?: boolean;
  /** The Value stage's outcome filter; a reference never shows the filter. */
  outcomeFilter?: string | null;
  /** #731: false for the floor seats, whose cells carry no US$ money line. */
  showMoney?: boolean;
};

/**
 * The shared flow basis. `read` is the strip's own `flowLandingRead` output;
 * the rest is what that read was assembled from, handed back so a surface that
 * renders beside the strip (the Advisory route's stage bodies, its case drawer,
 * its role screen) reads one basis rather than a second copy of the inputs.
 */
export type AdvisoryFlowRead = {
  /** The strip's read; null until every case fixture's frames are in hand. */
  read: FlowLandingRead | null;
  /** The input that read was computed from, null on the same gate. The Workflows
   *  catalogue counts open cases per Priority actions run on it (K19). */
  input: FlowLandingReadInput | null;
  /** The recorded investigation case's id (the strip's replay case), if any. */
  replayCaseId: string | undefined;
  diagnosis: Diagnosis;
  /** The role-aware session cases — `flowLandingRead`'s `roleCases`. */
  cases: readonly Case[];
  /** The threaded cases the flow prices (deduped by id) — its `cases` input. */
  pipelineCases: readonly Case[];
  /** The threaded cases before dedup — what the case drawer and rule set read. */
  casesForDisplay: readonly Case[];
  actions: readonly RecommendedAction[];
  reviews: readonly FMEAUpdateReview[];
  stageTimelines: CaseStageTimeline;
};

export function useAdvisoryFlowRead(options: AdvisoryFlowReadOptions = {}): AdvisoryFlowRead {
  const withInvestigationRuns = options.withInvestigationRuns ?? false;
  const outcomeFilter = options.outcomeFilter ?? null;
  const showMoney = options.showMoney ?? true;
  const { state, diagnosis: scenarioDiagnosis } = useScenario();
  const advisory = useAdvisory();
  const replayFixtures = useMemo(() => caseReplayFixtures(CASES), []);
  const replayByFixture = useAnalysisReplays(replayFixtures);
  const replayCase = useMemo(() => recordedInvestigationCase(CASES), []);
  const replayCaseId = replayCase?.id;
  const replayFixture = replayCase?.analysisRun?.fixture;
  const replayReady = replayFixtures.every((fixture) => replayByFixture[fixture] !== undefined);
  const analysisFrames = useMemo(
    () => analysisFramesForPipeline(replayFixture ? replayByFixture[replayFixture] : undefined),
    [replayByFixture, replayFixture],
  );

  const diagnosis = useMemo(() => {
    const hasDiagnosticOverrides = advisory.reviewedChanges.size > 0 ||
      advisory.approvedChangeIds.size > 0 || advisory.rejectedChanges.size > 0 ||
      advisory.rulings.length > 0 || advisory.answeredEvidence.size > 0;
    return hasDiagnosticOverrides ? diagnose(state, advisory) : scenarioDiagnosis;
  }, [state, scenarioDiagnosis, advisory]);
  const cases = useMemo(() => casesWithSession(state, advisory, diagnosis), [state, advisory, diagnosis]);
  const actions = useMemo(() => recommendedActionsWithSession(advisory), [advisory]);
  const reviews = useMemo(() => fmeaReviewsWithSession(advisory), [advisory]);
  const previousShiftCursor = useMemo(() => addMinutes(shiftOf(state.cursor).start, -1), [state.cursor]);
  const previousShiftExcursionCount = useMemo(
    () => excursions({ ...state, cursor: previousShiftCursor }).length,
    [state, previousShiftCursor],
  );
  const casesForDisplay = useMemo(
    () => mergeCasesForThreading(cases, diagnosis),
    [cases, diagnosis],
  );
  const pipelineCases = useMemo(
    () => [...new Map(casesForDisplay.map((caseItem) => [caseItem.id, caseItem])).values()],
    [casesForDisplay],
  );
  const stageTimelines = useMemo(
    () => buildCaseStageTimeline(CASES, actions, reviews, replayByFixture),
    [actions, reviews, replayByFixture],
  );

  // K15 / D24: the recorded run comes from the committed trace, the rest from
  // the session's started runs. A surface that does not render them passes no
  // fixture, so `useAgentTrace` makes no request.
  const zincTrace = useAgentTrace(withInvestigationRuns ? replayFixture : undefined);
  const investigationRuns = useMemo(() => {
    if (!withInvestigationRuns) return undefined;
    const recorded = zincTrace.status === 'ready' ? recordedRunForTrace(CASES, zincTrace.trace) : null;
    const approvedAtByCase = new Map<string, SeriesTime>();
    for (const run of [...advisory.investigationRuns, ...(recorded ? [recorded] : [])]) {
      const approval = fmeaActionApproval(advisory.fmeaLearning, run.caseId);
      if (approval) approvedAtByCase.set(run.caseId, approval.at as SeriesTime);
    }
    return investigationRunRows({
      recorded,
      session: advisory.investigationRuns,
      cursor: state.cursor,
      approvedAtByCase,
      playingRunId: advisory.playingInvestigationRunId,
    });
  }, [withInvestigationRuns, zincTrace, advisory.investigationRuns, advisory.fmeaLearning,
    advisory.playingInvestigationRunId, state.cursor]);

  const input = useMemo((): FlowLandingReadInput | null => {
    if (!replayReady) return null;
    return {
      state,
      cases: pipelineCases,
      roleCases: cases,
      actions,
      reviews,
      analysisFrames,
      analysisFixture: replayFixture,
      replayByFixture,
      stageTimelines,
      diagnosis,
      previousShiftExcursionCount,
      tagMappings: TAG_MAPPING,
      driverTreeSeed: DRIVER_TREE_SEED,
      failureModes: FAILURE_MODES,
      fmeaLearning: advisory.fmeaLearning,
      outcomeFilter,
      investigationRuns,
      showMoney,
    };
  }, [replayReady, state, pipelineCases, cases, actions, reviews, analysisFrames, replayFixture, replayByFixture,
    stageTimelines, diagnosis, previousShiftExcursionCount, advisory.fmeaLearning, outcomeFilter, investigationRuns,
    showMoney]);

  const read = useMemo(() => (input ? flowLandingRead(input) : null), [input]);

  return useMemo(() => ({
    read,
    input,
    replayCaseId,
    diagnosis,
    cases,
    pipelineCases,
    casesForDisplay,
    actions,
    reviews,
    stageTimelines,
  }), [read, input, replayCaseId, diagnosis, cases, pipelineCases, casesForDisplay, actions, reviews, stageTimelines]);
}

/**
 * The flow's input alone, for a surface that derives its own figures from the
 * same case basis the Advisory strip reads (the Workflows catalogue, K19).
 */
export function useAdvisoryFlowInput(): FlowLandingReadInput | null {
  return useAdvisoryFlowRead().input;
}
